/**
 * Visitor tracking hook for Fukkoni website.
 * Tracks page visits and stores a unique visitor ID in localStorage.
 */
import { useEffect, useRef } from 'react';
import { apiClient } from '../lib/axios';

const VISITOR_ID_KEY = 'fukkoni_visitor_id';

interface TrackVisitPayload {
  page: string;
  referrer: string | null;
  visitor_id: string | null;
}

/**
 * Get or create a unique visitor ID
 */
function getVisitorId(): string | null {
  try {
    return localStorage.getItem(VISITOR_ID_KEY);
  } catch {
    return null;
  }
}

/**
 * Save visitor ID to localStorage
 */
function saveVisitorId(visitorId: string): void {
  try {
    localStorage.setItem(VISITOR_ID_KEY, visitorId);
  } catch {
    // Ignore storage errors
  }
}

/**
 * Track a page visit
 */
async function trackPageVisit(page: string): Promise<void> {
  try {
    const payload: TrackVisitPayload = {
      page,
      referrer: document.referrer || null,
      visitor_id: getVisitorId(),
    };

    const response = await apiClient.post('/api/track-visit', payload);
    
    // Save the visitor ID for future visits
    if (response.data?.visitor_id) {
      saveVisitorId(response.data.visitor_id);
    }
  } catch (error) {
    // Silently fail - don't interrupt user experience
    console.debug('Visitor tracking failed:', error);
  }
}

/**
 * Track if we have already tracked the page visit in this session.
 * Using a module-level variable survives React Strict Mode re-mounts.
 */
let hasTrackedInSession = false;

/**
 * Hook to track page visits automatically
 * Call this in your main App component or layout
 */
export function useVisitorTracking(): void {
  useEffect(() => {
    // Only track once per session/page load
    if (hasTrackedInSession) return;
    hasTrackedInSession = true;

    // Track after a short delay to not block initial render
    // Use a local variable to ensure we don't clear it in cleanup 
    // if it's just a Strict Mode remount
    trackPageVisit(window.location.pathname);
  }, []);
}

/**
 * Manually track a specific page or event
 */
export function trackEvent(page: string): void {
  trackPageVisit(page);
}

export default useVisitorTracking;
